import { FormLabel, InfoPopover, Typography } from '@automata/ui';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { Button, Chip, IconButton, Stack, TextField } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';

import { FormSectionLabel } from './components/FormSectionLabel';
import {
  TimeConstraintModal,
  TimeConstraintModalData,
} from './components/TimeConstraintModal/TimeConstraintModal';
import { SidebarWrap } from './SidebarWrap';
import { deleteNodesEdges } from '../../features/actions';
import {
  addConsistencyTarget,
  addHardTimeConstraint,
  addTimingTarget,
  deleteConsistencyTarget,
  deleteHardTimeConstraint,
  deleteTimingTarget,
  deselectNodesEdges,
  editHardTimeConstraint,
  editTimeConstraintData,
  editTimingTarget,
} from '../../features/graph/graphSlice';
import { selectTimeConstraintInstances } from '../../features/graph/selectors';
import { selectNewlyCreatedTimeConstraintId } from '../../features/ui/selectors';
import { setNewlyCreatedTimeConstraintId } from '../../features/ui/uiSlice';
import { selectTaskPropertiesStrict } from '../../features/workflow/selectors';
import { useAppDispatch, useAppSelector } from '../../hooks';

const TIMING_RULE_TOOLTIP = `<b>Time Constraint</b><br/>
Enforces a strict timing window between two task edges. You can define a minimum time, a maximum time, or both. If the workflow cannot satisfy this constraint at runtime, execution stops.
<br/><br/><b>Timing Target</b><br/>
Defines an ideal time between two task edges. This is a soft constraint: if the exact target cannot be met at runtime, execution continues and the system adjusts to get as close as possible.`;
const EMPTY_RULES_COPY =
  'No timing rules set between these two tasks yet. Add a new rule to get started.';

type Props = {
  timeConstraintId: string;
};

export const TimeConstraintSidebar = ({ timeConstraintId }: Props) => {
  const dispatch = useAppDispatch();
  const [modalOpen, setModalOpen] = useState(false);
  const isSavingRef = useRef(false);
  const isNewlyCreatedRef = useRef(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [editingTimingTargetId, setEditingTimingTargetId] = useState<
    string | undefined
  >(undefined);
  const [editingHardConstraintId, setEditingHardConstraintId] = useState<
    string | undefined
  >(undefined);
  const [editingConsistencyTargetId, setEditingConsistencyTargetId] = useState<
    string | undefined
  >(undefined);
  const [initialRuleType, setInitialRuleType] = useState<
    'time_constraint' | 'timing_target' | 'consistency_target'
  >('time_constraint');
  const timeConstraints = useAppSelector(selectTimeConstraintInstances);
  const taskPropertiesById = useAppSelector(selectTaskPropertiesStrict);
  const newlyCreatedTimeConstraintId = useAppSelector(
    selectNewlyCreatedTimeConstraintId
  );

  const timeConstraint = timeConstraints[timeConstraintId];
  const {
    source,
    target,
    minDuration,
    maxDuration,
    enableDetailedCoverageCalculation,
  } = timeConstraint ?? {};
  const detailedCoverageEnabled = enableDetailedCoverageCalculation ?? true;

  const handleOpenAdd = useCallback(() => {
    setModalMode('add');
    setEditingTimingTargetId(undefined);
    setEditingHardConstraintId(undefined);
    setEditingConsistencyTargetId(undefined);
    setInitialRuleType('time_constraint');
    setModalOpen(true);
  }, [setModalMode, setModalOpen]);

  const handleOpenEditLegacy = useCallback(() => {
    setModalMode('edit');
    setEditingTimingTargetId(undefined);
    setEditingHardConstraintId(undefined);
    setEditingConsistencyTargetId(undefined);
    setInitialRuleType('time_constraint');
    setModalOpen(true);
  }, [setModalMode, setModalOpen]);

  const handleSaveRule = useCallback(
    (data: TimeConstraintModalData) => {
      isSavingRef.current = true;
      setModalOpen(false);
      const isEditing = modalMode === 'edit';
      const isRuleTypeChanging =
        isEditing &&
        initialRuleType !== undefined &&
        initialRuleType !== data.kind;

      if (isRuleTypeChanging) {
        if (initialRuleType === 'time_constraint') {
          if (editingHardConstraintId) {
            dispatch(
              deleteHardTimeConstraint({
                connectionId: timeConstraintId,
                hardConstraintId: editingHardConstraintId,
              })
            );
          } else {
            dispatch(
              editTimeConstraintData({
                id: timeConstraintId,
                minDuration: null,
                maxDuration: null,
              })
            );
          }
        } else if (
          initialRuleType === 'timing_target' &&
          editingTimingTargetId
        ) {
          dispatch(
            deleteTimingTarget({
              connectionId: timeConstraintId,
              timingTargetId: editingTimingTargetId,
            })
          );
        } else if (
          initialRuleType === 'consistency_target' &&
          editingConsistencyTargetId
        ) {
          dispatch(
            deleteConsistencyTarget({
              connectionId: timeConstraintId,
              consistencyTargetId: editingConsistencyTargetId,
            })
          );
        }
      }

      if (data.kind === 'time_constraint') {
        const shouldAddHardConstraint =
          modalMode === 'add' ||
          (editingHardConstraintId === undefined && isRuleTypeChanging);

        if (shouldAddHardConstraint) {
          dispatch(
            addHardTimeConstraint({
              connectionId: timeConstraintId,
              minDuration: data.minDuration ?? null,
              maxDuration: data.maxDuration ?? null,
              enableDetailedCoverageCalculation:
                data.enableDetailedCoverageCalculation,
              autoIgnoreIfBroken: data.autoIgnoreIfBroken,
            })
          );
        } else if (
          modalMode === 'edit' &&
          !editingHardConstraintId &&
          !isRuleTypeChanging
        ) {
          dispatch(
            editTimeConstraintData({
              id: timeConstraintId,
              minDuration: data.minDuration ?? null,
              maxDuration: data.maxDuration ?? null,
              enableDetailedCoverageCalculation:
                data.enableDetailedCoverageCalculation,
              autoIgnoreIfBroken: data.autoIgnoreIfBroken,
            })
          );
        } else if (editingHardConstraintId) {
          dispatch(
            editHardTimeConstraint({
              connectionId: timeConstraintId,
              hardConstraintId: editingHardConstraintId,
              patch: {
                minDuration: data.minDuration ?? null,
                maxDuration: data.maxDuration ?? null,
                enableDetailedCoverageCalculation:
                  data.enableDetailedCoverageCalculation,
                autoIgnoreIfBroken: data.autoIgnoreIfBroken,
              },
            })
          );
        }
      } else if (data.kind === 'consistency_target') {
        if (
          modalMode === 'add' ||
          !editingConsistencyTargetId ||
          isRuleTypeChanging
        ) {
          dispatch(
            addConsistencyTarget({
              connectionId: timeConstraintId,
            })
          );
        }
      } else if (data.kind === 'timing_target') {
        if (
          modalMode === 'add' ||
          !editingTimingTargetId ||
          isRuleTypeChanging
        ) {
          dispatch(
            addTimingTarget({
              connectionId: timeConstraintId,
              target: data.target,
              weight: data.weight ?? 1,
            })
          );
        } else {
          dispatch(
            editTimingTarget({
              connectionId: timeConstraintId,
              timingTargetId: editingTimingTargetId,
              patch: { target: data.target, weight: data.weight },
            })
          );
        }
      }
      setModalOpen(false);
    },
    [
      dispatch,
      timeConstraintId,
      setModalOpen,
      modalMode,
      editingTimingTargetId,
      editingHardConstraintId,
      editingConsistencyTargetId,
      initialRuleType,
    ]
  );

  const handleDeleteRule = useCallback(() => {
    let lastRule = false;
    if (editingTimingTargetId) {
      lastRule = (timeConstraint?.timingTargets?.length ?? 0) === 1;
      dispatch(
        deleteTimingTarget({
          connectionId: timeConstraintId,
          timingTargetId: editingTimingTargetId,
        })
      );
    } else if (editingConsistencyTargetId) {
      lastRule = (timeConstraint?.consistencyTargets?.length ?? 0) === 1;
      dispatch(
        deleteConsistencyTarget({
          connectionId: timeConstraintId,
          consistencyTargetId: editingConsistencyTargetId,
        })
      );
    } else if (editingHardConstraintId) {
      lastRule = (timeConstraint?.hardConstraints?.length ?? 0) === 1;
      dispatch(
        deleteHardTimeConstraint({
          connectionId: timeConstraintId,
          hardConstraintId: editingHardConstraintId,
        })
      );
    } else {
      // legacy single hard-constraint case: delete entire connection
      lastRule = true;
      dispatch(deleteNodesEdges({ edgeIds: [timeConstraintId] }));
    }

    if (
      lastRule &&
      !(minDuration !== null || maxDuration !== null) &&
      (timeConstraint?.timingTargets?.length ?? 0) +
        (timeConstraint?.consistencyTargets?.length ?? 0) +
        (timeConstraint?.hardConstraints?.length ?? 0) <=
        1
    ) {
      dispatch(deleteNodesEdges({ edgeIds: [timeConstraintId] }));
    }

    setModalOpen(false);
  }, [
    dispatch,
    timeConstraintId,
    setModalOpen,
    editingTimingTargetId,
    editingConsistencyTargetId,
    editingHardConstraintId,
    timeConstraint,
    minDuration,
    maxDuration,
  ]);

  useEffect(() => {
    if (newlyCreatedTimeConstraintId === timeConstraintId) {
      isNewlyCreatedRef.current = true;
      handleOpenAdd();
      dispatch(setNewlyCreatedTimeConstraintId(null));
    }
  }, [newlyCreatedTimeConstraintId, timeConstraintId, dispatch, handleOpenAdd]);

  if (timeConstraint === undefined) return null;

  const taskAName =
    source !== undefined
      ? taskPropertiesById[source]?.nickname ?? 'Untitled task'
      : 'Untitled task';
  const taskBName =
    target !== undefined
      ? taskPropertiesById[target]?.nickname ?? 'Untitled task'
      : 'Untitled task';
  const hasRules =
    minDuration !== null ||
    maxDuration !== null ||
    (timeConstraint?.hardConstraints?.length ?? 0) > 0 ||
    (timeConstraint?.timingTargets?.length ?? 0) > 0 ||
    (timeConstraint?.consistencyTargets?.length ?? 0) > 0;

  const handleCloseModal = () => {
    if (!isSavingRef.current && !hasRules) {
      dispatch(deleteNodesEdges({ edgeIds: [timeConstraintId] }));
    }
    isSavingRef.current = false;
    setEditingTimingTargetId(undefined);
    setEditingHardConstraintId(undefined);
    setEditingConsistencyTargetId(undefined);
    setModalOpen(false);
  };

  const handleCloseSidebar = () => {
    if (!hasRules) {
      dispatch(deleteNodesEdges({ edgeIds: [timeConstraintId] }));
    }
    dispatch(deselectNodesEdges());
  };

  const getDurationLabel = (value: number | null | undefined) => {
    if (value === null || value === undefined) return 'None';
    const hours = Math.floor(value / 3600);
    const minutes = Math.floor((value % 3600) / 60);
    const seconds = value % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const getInitialData = (): TimeConstraintModalData | undefined => {
    if (initialRuleType === 'timing_target' && editingTimingTargetId) {
      const tt = timeConstraint?.timingTargets?.find(
        (t) => t.id === editingTimingTargetId
      );
      if (tt) {
        return {
          kind: 'timing_target',
          target: tt.target,
          weight: tt.weight,
          timingTargetId: editingTimingTargetId,
        };
      }
    }
    if (initialRuleType === 'time_constraint' && editingHardConstraintId) {
      const hc = timeConstraint?.hardConstraints?.find(
        (h) => h.id === editingHardConstraintId
      );
      if (hc) {
        return {
          kind: 'time_constraint',
          minDuration: hc.minDuration,
          maxDuration: hc.maxDuration,
          enableDetailedCoverageCalculation:
            hc.enableDetailedCoverageCalculation,
          autoIgnoreIfBroken: hc.autoIgnoreIfBroken,
          hardConstraintId: editingHardConstraintId,
        };
      }
    }
    if (
      initialRuleType === 'consistency_target' &&
      editingConsistencyTargetId
    ) {
      const ct = timeConstraint?.consistencyTargets?.find(
        (c) => c.id === editingConsistencyTargetId
      );
      if (ct) {
        return {
          kind: 'consistency_target',
          consistencyTargetId: editingConsistencyTargetId,
        };
      }
    }
    if (
      initialRuleType === 'time_constraint' &&
      !editingHardConstraintId &&
      (minDuration !== null || maxDuration !== null)
    ) {
      return {
        kind: 'time_constraint',
        minDuration,
        maxDuration,
        enableDetailedCoverageCalculation: detailedCoverageEnabled,
        autoIgnoreIfBroken: timeConstraint?.autoIgnoreIfBroken ?? false,
      };
    }
    return undefined;
  };

  return (
    <SidebarWrap handleCloseSidebar={handleCloseSidebar} showComments={false}>
      <Stack gap={2}>
        <FormSectionLabel>Selected tasks</FormSectionLabel>
        <Stack gap={0.5}>
          <Typography variant="overline" color="text.secondary">
            Task A
          </Typography>
          <TextField value={taskAName} disabled size="small" fullWidth />
        </Stack>

        <Stack gap={0.5}>
          <Typography variant="overline" color="text.secondary">
            Task B
          </Typography>
          <TextField value={taskBName} size="small" fullWidth disabled />
        </Stack>

        <Stack spacing={2}>
          <Stack direction="row" gap={1} alignItems="center">
            <FormLabel infoPopover={<InfoPopover copy={TIMING_RULE_TOOLTIP} />}>
              <Typography variant="h4" textTransform="none">
                Timing rules
              </Typography>
            </FormLabel>
          </Stack>
        </Stack>

        {hasRules ? (
          <Stack spacing={1.5}>
            {(timeConstraint?.hardConstraints?.length ?? 0) > 0 ? (
              <Stack gap={1.5}>
                {(timeConstraint?.hardConstraints ?? []).map((hc) => (
                  <Stack
                    key={hc.id}
                    spacing={1.25}
                    sx={(theme) => ({
                      border: `1px solid ${theme.palette.grey[300]}`,
                      borderRadius: 2,
                      padding: 1.5,
                    })}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                    >
                      <Stack spacing={0.5}>
                        <Stack direction="row" alignItems="center" gap={1}>
                          <Typography variant="body2" fontWeight="medium">
                            Time constraint
                          </Typography>
                          <Chip
                            label="hard"
                            size="small"
                            sx={{
                              backgroundColor: '#ffe2e2',
                              border: '1px solid #ffc9c9',
                              color: '#c10007',
                              fontWeight: 500,
                              height: 22,
                              borderRadius: '999px',
                              '.MuiChip-label': {
                                px: 1,
                              },
                            }}
                          />
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          Min: {getDurationLabel(hc.minDuration)} • Max:{' '}
                          {getDurationLabel(hc.maxDuration)}
                        </Typography>
                      </Stack>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setModalMode('edit');
                          setEditingTimingTargetId(undefined);
                          setEditingHardConstraintId(hc.id);
                          setEditingConsistencyTargetId(undefined);
                          setInitialRuleType('time_constraint');
                          setModalOpen(true);
                        }}
                        aria-label="Edit time constraint"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                    <Stack spacing={0.5}>
                      <Typography variant="caption" color="text.secondary">
                        Detailed coverage:{' '}
                        {hc.enableDetailedCoverageCalculation ? 'ON' : 'OFF'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Auto Ignore: {hc.autoIgnoreIfBroken ? 'ON' : 'OFF'}
                      </Typography>
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            ) : null}

            {/* Legacy single hard constraint */}
            {(timeConstraint?.hardConstraints?.length ?? 0) === 0 &&
            (minDuration !== null || maxDuration !== null) ? (
              <Stack
                spacing={1.25}
                sx={(theme) => ({
                  border: `1px solid ${theme.palette.grey[300]}`,
                  borderRadius: 2,
                  padding: 1.5,
                })}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                >
                  <Stack spacing={0.5}>
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Typography variant="body2" fontWeight="medium">
                        Time constraint
                      </Typography>
                      <Chip
                        label="hard"
                        size="small"
                        sx={{
                          backgroundColor: '#ffe2e2',
                          border: '1px solid #ffc9c9',
                          color: '#c10007',
                          fontWeight: 500,
                          height: 22,
                          borderRadius: '999px',
                          '.MuiChip-label': {
                            px: 1,
                          },
                        }}
                      />
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      Min: {getDurationLabel(minDuration)} • Max:{' '}
                      {getDurationLabel(maxDuration)}
                    </Typography>
                  </Stack>
                  <IconButton
                    size="small"
                    onClick={handleOpenEditLegacy}
                    aria-label="Edit time constraint"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Stack>
                <Stack spacing={0.5}>
                  <Typography variant="caption" color="text.secondary">
                    Detailed coverage: {detailedCoverageEnabled ? 'ON' : 'OFF'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Auto Ignore:{' '}
                    {timeConstraint.autoIgnoreIfBroken ? 'ON' : 'OFF'}
                  </Typography>
                </Stack>
              </Stack>
            ) : null}

            {/* Consistency targets list */}
            {(timeConstraint?.consistencyTargets ?? []).map((ct) => (
              <Stack
                key={ct.id}
                spacing={1.25}
                sx={(theme) => ({
                  border: `1px solid ${theme.palette.grey[300]}`,
                  borderRadius: 2,
                  padding: 1.5,
                })}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                >
                  <Stack spacing={0.5}>
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Typography variant="body2" fontWeight="medium">
                        Consistency target
                      </Typography>
                      <Chip
                        label="soft"
                        size="small"
                        sx={{
                          backgroundColor: '#fef3c6',
                          border: '1px solid #fee685',
                          color: '#bb4d00',
                          fontWeight: 500,
                          height: 22,
                          borderRadius: '999px',
                          '.MuiChip-label': {
                            px: 1,
                          },
                        }}
                      />
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      Consistency: ON
                    </Typography>
                  </Stack>
                  <IconButton
                    size="small"
                    aria-label="Edit consistency target"
                    onClick={() => {
                      setModalMode('edit');
                      setEditingTimingTargetId(undefined);
                      setEditingHardConstraintId(undefined);
                      setEditingConsistencyTargetId(ct.id);
                      setInitialRuleType('consistency_target');
                      setModalOpen(true);
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>
            ))}

            {/* Timing targets list */}
            <Stack gap={1.5}>
              {(timeConstraint?.timingTargets ?? []).map((tt) => (
                <Stack
                  key={tt.id}
                  spacing={1.25}
                  sx={(theme) => ({
                    border: `1px solid ${theme.palette.grey[300]}`,
                    borderRadius: 2,
                    padding: 1.5,
                  })}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                  >
                    <Stack spacing={0.5}>
                      <Stack direction="row" alignItems="center" gap={1}>
                        <Typography variant="body2" fontWeight="medium">
                          Timing target
                        </Typography>
                        <Chip
                          label="soft"
                          size="small"
                          sx={{
                            backgroundColor: '#fef3c6',
                            border: '1px solid #fee685',
                            color: '#bb4d00',
                            fontWeight: 500,
                            height: 22,
                            borderRadius: '999px',
                            '.MuiChip-label': {
                              px: 1,
                            },
                          }}
                        />
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        Ideal time: {getDurationLabel(tt.target)}
                      </Typography>
                    </Stack>
                    <IconButton
                      size="small"
                      aria-label="Edit timing target"
                      onClick={() => {
                        setModalMode('edit');
                        setEditingHardConstraintId(undefined);
                        setEditingConsistencyTargetId(undefined);
                        setEditingTimingTargetId(tt.id);
                        setInitialRuleType('timing_target');
                        setModalOpen(true);
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                  <Stack spacing={0.5}>
                    <Typography variant="caption" color="text.secondary">
                      Priority: {tt.weight}/10
                    </Typography>
                  </Stack>
                </Stack>
              ))}
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenAdd}
              >
                ADD RULE
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Stack gap={2}>
            <Typography variant="body2" color="text.secondary">
              {EMPTY_RULES_COPY}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenAdd}
            >
              ADD RULE
            </Button>
          </Stack>
        )}
      </Stack>
      <TimeConstraintModal
        open={modalOpen}
        mode={modalMode}
        onClose={handleCloseModal}
        onSave={handleSaveRule}
        onDelete={modalMode === 'edit' ? handleDeleteRule : undefined}
        initialRuleType={initialRuleType}
        initialData={getInitialData()}
        hasConsistencyRule={
          (timeConstraint?.consistencyTargets?.length ?? 0) > 0
        }
      />
    </SidebarWrap>
  );
};
